import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActionType, IMessage, MessageStatus } from '@bciti/types';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { MessageService } from 'src/app/services/message/message.service';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { State } from 'src/app/store';
import { organisationsLoadingSelector, organisationsSelector } from 'src/app/store/global/global.selectors';
import { MessageDetailDialogComponent } from './message-detail-dialog/message-detail-dialog.component';

@Component({
    selector: 'app-messages-form',
    templateUrl: './messages-form.component.html',
    styleUrls: ['./messages-form.component.scss'],
})
export class MessagesFormComponent implements OnInit, AfterViewInit, OnDestroy {
    destroy$ = new Subject();

    form: FormGroup;
    loading = false;
    orgsLoading = false;
    organisations: string[] = [];
    connectorActionTypes: ActionType[] = Object.values(ActionType);
    messageStatusTypes: MessageStatus[] = Object.values(MessageStatus);
    maxDate = new Date();
    displayedColumns: string[] = ['organisation', 'connector', 'action', 'status', 'errors', 'userId', 'processDate'];

    results = new MatTableDataSource<IMessage>([]);

    retryTtl = 2;

    isLoading$: Observable<Boolean> = this.store.select(organisationsLoadingSelector).pipe(
        takeUntil(this.destroy$),
        map((loading) => loading),
    );

    constructor(
        private fb: FormBuilder,
        private organisationService: OrganisationService,
        private toastService: ToastrService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private store: Store<State>,
    ) {}

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    ngOnInit(): void {
        this.store
            .select(organisationsSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((organisations) => {
                    this.organisations = ['ALL', ...organisations.data.map((org) => org.tag)];
                }),
            )
            .subscribe();

        this.form = this.fb.group(
            {
                organisation: new FormControl(''),
                actionTypes: new FormControl(''),
                statusTypes: new FormControl([]),
                userId: new FormControl(''),
                dateStart: new FormControl(''),
                dateEnd: new FormControl(''),
            },
            {
                updateOn: 'change',
            },
        );
    }

    ngAfterViewInit() {
        this.results.sort = this.sort;
        this.results.paginator = this.paginator;
    }

    getMessages() {
        if (this.form.valid) {
            this.loading = true;
            this.messageService
                .getMessages(
                    this.form.get('organisation').value,
                    this.form.get('actionTypes').value,
                    this.form.get('statusTypes').value,
                    this.form.get('userId').value,
                    this.form.get('dateStart').value,
                    this.form.get('dateEnd').value,
                )
                .pipe(
                    takeUntil(this.destroy$),
                    map((res) => {
                        this.results.data = res;
                        this.loading = false;
                    }),
                    catchError((error) => {
                        this.toastService.error(error);
                        this.loading = false;
                        return of([]);
                    }),
                )
                .subscribe();
        }
    }

    applyFilter(e: Event) {
        const filterValue = (e.target as HTMLInputElement).value;
        this.results.filter = filterValue.trim().toLowerCase();
    }

    viewDetail(e) {
        console.log(e);
        this.dialog.open(MessageDetailDialogComponent, {
            data: e,
            width: '600px',
        });
    }

    isRetryMessage(queueMessage): boolean {
        let jsonQueueMessage = JSON.parse(queueMessage);
        return jsonQueueMessage.ttl < this.retryTtl ? true : false;
    }

    getUserId(queueMessage) {
        let jsonQueueMessage = JSON.parse(queueMessage);
        return jsonQueueMessage?.data?.user1 && jsonQueueMessage?.data?.user2 ? `${jsonQueueMessage.data.user1}\n${jsonQueueMessage.data.user2}` : jsonQueueMessage?.data?.userId ?? '';
    }

    handleErrors(errorList: string[]) {
        let errors = '';
        for (const error of errorList) {
            const statusCodeMatch = error.match(/status code (\d+)/);
            if (statusCodeMatch) {
                const statusCode = parseInt(statusCodeMatch[1], 10);
                const message = statusMessages[statusCode] || 'Unknown Error';
                errors += message;
            }
        }
        return errors;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

export enum HttpStatus {
    RetryWith = 449,
    InternalServerError = 500,
    NotFound = 404,
    Conflict = 409,
}

export const statusMessages: Record<HttpStatus, string> = {
    [HttpStatus.RetryWith]: '449 - Retry',
    [HttpStatus.InternalServerError]: '500 - Internal Server Error',
    [HttpStatus.NotFound]: '404 - Not Found',
    [409]: '409 - Conflict exception',
};
